/**
 * Client-side handling of the stored session token.
 *
 * Guest tokens (order lookup / return without sales order) are short-lived;
 * code-login tokens (verifyAuthCode) are not guest tokens and are never
 * touched here. Payloads are only decoded, not verified: the API does that.
 */

const TOKEN_KEY = 'token';
const USER_KEY = 'user';

// Pages that only work inside a guest return journey.
const GUEST_JOURNEY_PATH_PREFIXES = ['/return/', '/payment'];

type TokenPayload = {
  exp?: number;
  guest?: boolean;
  [key: string]: unknown;
};

export const decodeTokenPayload = (token: string): TokenPayload | null => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = decodeURIComponent(
      Array.from(atob(padded))
        .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join(''),
    );
    const payload = JSON.parse(json);
    return payload && typeof payload === 'object' ? payload : null;
  } catch {
    return null;
  }
};

export const isGuestToken = (token: string | null): boolean =>
  Boolean(token) && decodeTokenPayload(token as string)?.guest === true;

export const isTokenExpired = (token: string, now = Date.now()): boolean => {
  const exp = decodeTokenPayload(token)?.exp;
  return typeof exp === 'number' && exp * 1000 <= now;
};

/**
 * Ends a guest session: removes a stored guest token and the guest user.
 * A code-login (non-guest) session is left untouched.
 */
export const clearGuestSession = () => {
  const token = localStorage.getItem(TOKEN_KEY);

  if (token && !isGuestToken(token)) {
    return;
  }

  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

export const isGuestJourneyPath = (pathname: string) =>
  GUEST_JOURNEY_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));

/**
 * The token to send with a request. An expired guest token is dropped
 * (together with the guest user) instead of being sent, so public pages keep
 * working and the consumer simply starts a new lookup.
 */
export const getUsableStoredToken = (): {
  token: string | null;
  guestSessionExpired: boolean;
} => {
  const token = localStorage.getItem(TOKEN_KEY);

  if (token && isGuestToken(token) && isTokenExpired(token)) {
    clearGuestSession();
    return { token: null, guestSessionExpired: true };
  }

  return { token, guestSessionExpired: false };
};
